import { PlatformStatementService } from "../resource";
import swal from "sweetalert2";
import i18n from "./i18n";

const PlatformStatementModule = {
    namespaced: true,
    state: {
        statements: [],
        statement: null,
        details: [],
        summary: null,
        isLoading: false,
        current_page: 1,
        total: 0
    },
    mutations: {
        SET_STATEMENTS(state, data) {
            state.statements = data;
        },
        SET_STATEMENT(state, data) {
            state.statement = data;
        },
        SET_DETAILS(state, data) {
            state.details = data;
        },
        SET_SUMMARY(state, data) {
            state.summary = data;
        },
        SET_LOADING(state, loading) {
            state.isLoading = loading;
        },
        SET_CURRENT_PAGE(state, page) {
            state.current_page = page;
        },
        SET_TOTAL(state, total) {
            state.total = total;
        }
    },
    actions: {
        async uploadStatement({ commit, state }, data) {
            commit("SET_LOADING", true);
            swal.fire({
                title: i18n.t(`processing`),
                allowEscapeKey: false,
                allowOutsideClick: false,
                onOpen: () => {
                    swal.showLoading();
                }
            });
            try {
                const response = await PlatformStatementService.UploadStatement(data);
                swal.close();
                commit("SET_LOADING", false);
                return response;
            } catch (err) {
                swal.close();
                commit("SET_LOADING", false);
                console.log(err);
                if (err.response && err.response.data) {
                    // Handle both old and new error response structures
                    const errorMessage = err.response.data.message || err.response.data.Msg || 'Error occurred';
                    const errorCause = err.response.data.errors ? err.response.data.errors.join(', ') : (err.response.data.Cause || '');
                    swal.fire({
                        title: i18n.t(errorMessage),
                        text: errorCause ? i18n.t(errorCause) : '',
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                } else {
                    swal.fire({
                        title: "Error",
                        text: "Network error or server unavailable",
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                }
                throw err;
            }
        },

        async listStatements({ commit, state }, data) {
            commit("SET_LOADING", true);
            swal.fire({
                title: i18n.t(`processing`),
                allowEscapeKey: false,
                allowOutsideClick: false,
                onOpen: () => {
                    swal.showLoading();
                }
            });
            try {
                const page = data.page || 1;
                const limit = data.limit || 20;
                const response = await PlatformStatementService.ListStatements(page, limit, data.filters);
                swal.close();

                // Handle both old and new API response structures
                // Old format: { Status: "ok", Data: [{ statements: [], total: 0 }] }
                // New format: { success: true, data: { statements: [], total: 0 } }
                let result = { statements: [], total: 0 };
                if (response.data.Data && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                    // Old format - Data is an array
                    result = response.data.Data[0];
                } else if (response.data.data) {
                    // New format - data is an object
                    result = response.data.data;
                }

                commit("SET_STATEMENTS", result.statements || []);
                commit("SET_TOTAL", result.total || 0);
                commit("SET_CURRENT_PAGE", page);
                commit("SET_LOADING", false);
            } catch (err) {
                swal.close();
                console.log(err);
                if (err.response && err.response.data) {
                    const errorMessage = err.response.data.message || err.response.data.Msg || 'Error occurred';
                    const errorCause = err.response.data.errors ? err.response.data.errors.join(', ') : (err.response.data.Cause || '');
                    swal.fire({
                        title: i18n.t(errorMessage),
                        text: errorCause ? i18n.t(errorCause) : '',
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                } else {
                    swal.fire({
                        title: "Error",
                        text: "Network error or server unavailable",
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                }
                commit("SET_STATEMENTS", []);
                commit("SET_LOADING", false);
            }
        },

        async getStatement({ commit, state }, id) {
            commit("SET_LOADING", true);
            swal.fire({
                title: i18n.t(`processing`),
                allowEscapeKey: false,
                allowOutsideClick: false,
                onOpen: () => {
                    swal.showLoading();
                }
            });
            try {
                const response = await PlatformStatementService.GetStatement(id);
                swal.close();

                // Handle both old and new API response structures
                // Old format: { Status: "ok", Data: [{ statement: {...}, details: [] }] }
                // New format: { success: true, data: {...} }
                let result = null;
                let details = [];
                if (response.data.Data && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                    // Old format - Data is an array
                    const dataObj = response.data.Data[0];
                    result = dataObj.statement || dataObj;
                    details = dataObj.details || [];
                } else if (response.data.data) {
                    // New format - data is an object
                    result = response.data.data;
                    details = response.data.data.details || [];
                }

                commit("SET_STATEMENT", result);
                commit("SET_DETAILS", details);
                commit("SET_LOADING", false);
            } catch (err) {
                swal.close();
                console.log(err);
                if (err.response && err.response.data) {
                    const errorMessage = err.response.data.message || err.response.data.Msg || 'Error occurred';
                    const errorCause = err.response.data.errors ? err.response.data.errors.join(', ') : (err.response.data.Cause || '');
                    swal.fire({
                        title: i18n.t(errorMessage),
                        text: errorCause ? i18n.t(errorCause) : '',
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                }
                commit("SET_STATEMENT", null);
                commit("SET_DETAILS", []);
                commit("SET_LOADING", false);
            }
        },

        async getSummary({ commit, state }, data) {
            commit("SET_LOADING", true);
            try {
                // Use platform-specific endpoint based on platform_id
                const response = await PlatformStatementService.GetPlatformSummary(
                    data.platform_id,
                    {
                        date_from: data.date_from,
                        date_to: data.date_to
                    }
                );

                // Handle both old and new API response structures
                // Old format: { Status: "ok", Data: [{...}] }
                // New format: { success: true, data: {...} }
                let result = null;
                if (response.data.Data && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                    // Old format - Data is an array
                    result = response.data.Data[0];
                } else if (response.data.data) {
                    // New format - data is an object
                    result = response.data.data;
                }

                commit("SET_SUMMARY", result);
                commit("SET_LOADING", false);
                return response;
            } catch (err) {
                console.log(err);
                if (err.response && err.response.data) {
                    // Handle both old and new error response structures
                    const errorMessage = err.response.data.message || err.response.data.Msg || 'Error occurred';
                    const errorCause = err.response.data.errors ? err.response.data.errors.join(', ') : (err.response.data.Cause || '');
                    swal.fire({
                        title: i18n.t(errorMessage),
                        text: errorCause ? i18n.t(errorCause) : '',
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-info btn-fill"
                    });
                }
                commit("SET_SUMMARY", null);
                commit("SET_LOADING", false);
                throw err;
            }
        },

        async exportStatement({ commit, state }, id) {
            try {
                return await PlatformStatementService.ExportStatement(id);
            } catch (err) {
                console.log(err);
                if (err.response && err.response.data) {
                    swal.fire({
                        title: "Export Failed",
                        text: "Failed to export statement",
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-danger btn-fill"
                    });
                }
                throw err;
            }
        },

        async deleteStatement({ commit, state }, id) {
            try {
                return await PlatformStatementService.DeleteStatement(id);
            } catch (err) {
                console.log(err);
                if (err.response && err.response.data) {
                    const errorMessage = err.response.data.message || err.response.data.Msg || 'Error occurred';
                    const errorCause = err.response.data.errors ? err.response.data.errors.join(', ') : (err.response.data.Cause || '');
                    swal.fire({
                        title: i18n.t(errorMessage),
                        text: errorCause ? i18n.t(errorCause) : '',
                        icon: "error",
                        buttonsStyling: false,
                        confirmButtonClass: "btn btn-danger btn-fill"
                    });
                }
                throw err;
            }
        },

        setCurrentPage({ commit, state }, page) {
            commit("SET_CURRENT_PAGE", page);
        }
    },
    getters: {
        getStatements(state) {
            return state.statements;
        },
        getStatement(state) {
            return state.statement;
        },
        getDetails(state) {
            return state.details;
        },
        getSummary(state) {
            return state.summary;
        },
        isLoading(state) {
            return state.isLoading;
        },
        getCurrentPage(state) {
            return state.current_page;
        },
        getTotal(state) {
            return state.total;
        },
        getPlatformTable(state) {
            return [
                { id: 2, name: "TikTok" },
                { id: 3, name: "Shopee" },
                { id: 4, name: "Lazada" }
            ];
        },
        getPlatformTableWithAll(state) {
            return [
                { id: 0, name: "-- แพลตฟอร์มทั้งหมด --" },
                { id: 2, name: "TikTok" },
                { id: 3, name: "Shopee" },
                { id: 4, name: "Lazada" }
            ];
        },
        getPlatformName(state) {
            return (platformId) => {
                const platforms = {
                    2: 'tiktok',
                    3: 'shopee',
                    4: 'lazada'
                };
                return platforms[platformId] || '';
            };
        }
    }
};

export default PlatformStatementModule;
